import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { KeyboardShortcut, formatShortcutKey, groupShortcutsByCategory } from '@/hooks/use-keyboard-shortcuts'
import { cn } from '@/lib/utils'

interface KeyboardShortcutsHelpProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shortcuts: KeyboardShortcut[]
}

const categoryLabels: Record<string, string> = {
  playback: 'Playback',
  navigation: 'Navigation',
  capture: 'Capture',
  interface: 'Interface',
}

const categoryIcons: Record<string, string> = {
  playback: '▶️',
  navigation: '🎯',
  capture: '📸',
  interface: '🖥️',
}

export function KeyboardShortcutsHelp({
  open,
  onOpenChange,
  shortcuts,
}: KeyboardShortcutsHelpProps) {
  const groupedShortcuts = groupShortcutsByCategory(shortcuts)
  const categories = Object.keys(groupedShortcuts)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            ⌨️ Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-6 mt-4">
          {categories.map((category) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                <span>{categoryIcons[category]}</span>
                {categoryLabels[category] || category}
              </h3>
              <div className="grid gap-2">
                {groupedShortcuts[category].map((shortcut, index) => (
                  <div
                    key={index}
                    className={cn(
                      'flex items-center justify-between py-2 px-3 rounded-lg',
                      'bg-muted/50 hover:bg-muted transition-colors'
                    )}
                  >
                    <span className="text-sm">{shortcut.description}</span>
                    <kbd
                      className={cn(
                        'px-2.5 py-1.5 text-xs font-mono font-semibold',
                        'bg-background border border-border rounded-md shadow-sm',
                        'min-w-[2rem] text-center'
                      )}
                    >
                      {formatShortcutKey(shortcut)}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t text-center text-sm text-muted-foreground">
          Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">?</kbd> anytime to
          toggle this help panel
        </div>
      </DialogContent>
    </Dialog>
  )
}
